/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  extractClosestEdge,
  attachClosestEdge,
  type Edge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-indicator/box";
import { convertDate } from "@Services/luxonHelper";
import { manageChecklist } from "@Features/reviews/helpers";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import type { ManageChecklistRequest } from "@ServerTypes/reviews/reviews.schema";
import type { GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** One answer choice of a checklist — `manageChecklistRequest.choices`, an open `{ value, ... }` record. */
type ChecklistChoice = NonNullable<ManageChecklistRequest["choices"]>[number];

/**
 * The checklist row this panel is opened on, as it comes out of the checklist-list grid.
 * `getChecklistsResponse` types its `result` as `z.unknown()` (via `reviewStatusResponse`), so there
 * is no server row type for the listed document; only the fields read here are named.
 */
type ChecklistRow = GridRowBase & {
  _id?: string;
  title?: string;
  choices?: ChecklistChoice[];
  allowMultiple?: boolean;
  createdBy?: { userId?: ObjectIdString };
  createdDate?: number;
};

interface ChecklistPropertiesProps {
  checklist?: ChecklistRow;
  _id?: string | number;
  dataGrid: React.MutableRefObject<SlickGrid1Ref<ChecklistRow> | null>;
}

const ChecklistProperties: React.FC<ChecklistPropertiesProps> = ({ checklist, _id, dataGrid }) => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<ChecklistChoice[]>(checklist?.choices || []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [tempValue, setTempValue] = useState<string>("");
  const [allowMultiple, setAllowMultiple] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [openChoices, setOpenChoices] = useState<boolean>(true);
  const [openConditions, setOpenConditions] = useState<boolean>(true);

  const [closestEdges, setClosestEdges] = useState<Record<number, Edge | null>>({});

  const { portalSettingInfo } = useSelector((state: RootState) => state);

  const canEditChecklist = !!portalSettingInfo?.reviewFlowsChecklist?.editReviewFlowsChecklist?.enable;

  useEffect(() => {
    if (checklist?.choices?.length) {
      setChoices(checklist.choices);
    }
    if (typeof checklist?.allowMultiple === "boolean") setAllowMultiple(checklist.allowMultiple);
  }, [checklist]);

  const persist = async (nextChoices: ChecklistChoice[] = choices, nextAllowMultiple: boolean = allowMultiple) => {
    const idToSave = checklist?._id || _id;
    if (!idToSave) return;

    const response = await manageChecklist({
      // Grid-row / prop id; the manageChecklist route schema validates it server-side.
      _id: idToSave as ObjectIdString,
      choices: nextChoices,
      allowMultiple: nextAllowMultiple,
    });

    const responseData = response?.data?.data;
    if (responseData?.choices) {
      setChoices(responseData.choices);
    }

    // `SlickDataView.items` is `protected` in slickgrid-react's declarations but is the array this
    // code has always read at runtime; the cast only re-opens the declaration.
    const data = [...(dataGrid.current!.dataView as unknown as { items: ChecklistRow[] }).items];
    const index = data.findIndex((item) => item._id === idToSave);
    if (index !== -1 && responseData) {
      data[index] = {
        // `index !== -1` came from `findIndex`, so the slot exists.
        ...data[index]!,
        choices: responseData.choices,
        allowMultiple: responseData.allowMultiple,
      };
      dataGrid.current!.dataView.setItems(data);
    }
  };

  const reorderChoices = useCallback(
    (startIndex: number, indexOfTarget: number, closestEdgeOfTarget: Edge | null) => {
      const finishIndex = getReorderDestinationIndex({
        startIndex,
        closestEdgeOfTarget,
        indexOfTarget,
        axis: "vertical",
      });
      if (finishIndex === startIndex) return;

      setChoices((prev) => {
        const next = reorder({ list: prev, startIndex, finishIndex });
        persist(next);
        return next;
      });
    },
    [persist],
  );
  useEffect(() => {
    if (!canEditChecklist) return;
    // The monitor / drop-target callbacks below take their argument types from `monitorForElements`
    // and `dropTargetForElements`. Pragmatic-drag-and-drop types the payload `getInitialData` /
    // `getData` attaches as `Record<string, unknown>`, so the `type` / `index` keys this file puts in
    // there come back out as `unknown` and are cast at the point of use.
    return monitorForElements({
      canMonitor: ({ source }) => source.data?.type === "choice",
      onDrag: ({ location }) => {
        const newEdges: Record<number, Edge | null> = {};
        for (const target of location.current.dropTargets) {
          if (target.data?.type === "choice") {
            newEdges[target.data.index as number] = extractClosestEdge(target.data);
          }
        }
        setClosestEdges(newEdges);
      },
      onDrop: ({ location, source }) => {
        setClosestEdges({});
        const target = location.current.dropTargets[0];
        if (!target) return;

        const sourceData = source.data;
        const targetData = target.data;
        if (!sourceData || !targetData) return;

        const indexOfTarget = targetData.index;
        const closestEdgeOfTarget = extractClosestEdge(targetData);
        reorderChoices(sourceData.index as number, indexOfTarget as number, closestEdgeOfTarget);
      },
    });
  }, [choices, reorderChoices, canEditChecklist]);

  const choiceRefs = useRef<Record<number, HTMLElement | null>>({});

  const registerDraggableAndDropTarget = useCallback(
    (el: HTMLElement | null, index: number) => {
      if (!el || !canEditChecklist) return;
      choiceRefs.current[index] = el;

      const cleanups = [
        draggable({
          element: el,
          getInitialData: () => ({ type: "choice", index }),
        }),
        dropTargetForElements({
          element: el,
          canDrop: ({ source }) => source.data?.type === "choice",
          getData: ({ input }) =>
            attachClosestEdge({ type: "choice", index }, { element: el, input, allowedEdges: ["top", "bottom"] }),
        }),
      ];

      return () => cleanups.forEach((cleanup) => cleanup && cleanup());
    },
    [canEditChecklist],
  );

  const addChoice = () => {
    setChoices((prev) => {
      const next = [...prev, { value: "" }];
      setEditingIndex(next.length - 1);
      setTempValue("");
      return next;
    });
  };

  const confirmEdit = (index: number) => {
    if (tempValue.trim() !== "") {
      const next = [...choices];
      // `index` is the rendered choice's index, so the slot exists.
      next[index] = { ...next[index]!, value: tempValue.trim() };
      setChoices(next);
      persist(next);
    }
    setEditingIndex(null);
    setTempValue("");
  };

  const removeChoice = (index: number) => {
    if (choices.length <= 1) return;
    const next = choices.filter((_: ChecklistChoice, i: number) => i !== index);
    setChoices(next);
    persist(next);
  };

  return (
    <div
      className={`flex flex-col w-[32.5rem] bg-white border-s border-gray-200 h-full rounded-md shadow-sm ${
        !canEditChecklist ? "opacity-90 pointer-events-none" : ""
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col gap-1 flex-1">
          <h2 className="text-base font-semibold leading-tight text-gray-900 break-words whitespace-normal hyphens-auto">
            {untranslated(checklist!.title!)}
          </h2>
          <div className="flex items-center justify-between text-m text-gray-500">
            <UserNameTagComponent id={checklist?.createdBy?.userId} />
            <span>{convertDate(Number(checklist?.createdDate), "dateForText")}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-1">
        <div className="flex flex-col flex-1 overflow-auto">
          <Accordion expanded={openChoices} onChange={() => setOpenChoices(!openChoices)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className="flex items-center gap-3">
                <IconComponent name="ChoicesIcon" width="1.125rem" height="0.625rem" />
                <span className="font-medium">{t("Choices")}</span>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <ButtonComponent
                onClick={addChoice}
                disabled={!canEditChecklist || editingIndex !== null}
                className={`px-4 py-1.5 rounded-md text-sm font-medium
    ${
      editingIndex !== null
        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
        : "bg-gray-150 text-gray-800 hover:bg-gray-200"
    }`}
              >
                + {t("Add")}
              </ButtonComponent>
              <div className="flex flex-col mt-3 space-y-1">
                {/* `choices` entries are open records on the server, so `_id` / `value` arrive as
                    `unknown` and are cast where read below. */}
                {choices.map((choice: ChecklistChoice, index: number) => (
                  <div
                    key={(choice._id as string) || index}
                    ref={(el) => registerDraggableAndDropTarget(el, index)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className="relative flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-gray-50 cursor-grab border border-transparent hover:border-gray-200"
                  >
                    <span className="text-gray-400">{"☰"}</span>
                    {editingIndex === index ? (
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <TextFieldComponent
                            value={tempValue}
                            autoFocus
                            onChange={(e: { target: { value: string } }) => setTempValue(e.target.value)}
                            placeholder={t("Choice")}
                            slotProps={{
                              htmlInput: {
                                maxLength: 50,
                              },
                            }}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === "Enter") {
                                if (tempValue.trim() === "") {
                                  e.preventDefault();
                                  return;
                                }
                                confirmEdit(index);
                              }
                            }}
                            size="small"
                          />
                          <IconComponent
                            isEnabled={canEditChecklist}
                            name="tick"
                            width="1rem"
                            height="1rem"
                            className="text-gray-600 hover:text-gray-800 cursor-pointer"
                            onClick={() => {
                              if (tempValue.trim() === "") return;
                              confirmEdit(index);
                            }}
                          />
                          <IconComponent
                            isEnabled={canEditChecklist}
                            name="cross"
                            width="1rem"
                            height="1rem"
                            className="text-gray-600 hover:text-gray-800 cursor-pointer"
                            onClick={() => {
                              removeChoice(index);
                              setEditingIndex(null);
                              setTempValue("");
                            }}
                          />
                        </div>
                        {tempValue.length >= 50 && (
                          <span className="text-xs text-red-600">
                            {t("Please don't enter more than {{max, number}} characters", { max: 50 })}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-1 items-center justify-between">
                        <span className="text-m text-gray-800 truncate">{untranslated(choice.value as string)}</span>
                        {hoveredIndex === index && (
                          <div className="flex items-center gap-2">
                            <IconComponent
                              isEnabled={canEditChecklist}
                              name="edit"
                              className="text-gray-500 hover:text-gray-800 cursor-pointer"
                              onClick={() => {
                                setEditingIndex(index);
                                setTempValue(choice.value as string);
                              }}
                              width="1.25rem"
                              height="1.25rem"
                            />
                            <IconComponent
                              isEnabled={canEditChecklist}
                              name="delete"
                              className="text-gray-500 cursor-pointer"
                              onClick={() => removeChoice(index)}
                              width="1.25rem"
                              height="1.25rem"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {closestEdges[index] && <DropIndicator edge={closestEdges[index]} gap="1px" />}
                  </div>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={openConditions} onChange={() => setOpenConditions(!openConditions)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className="flex items-center gap-3">
                <IconComponent name="ConditionIcon" width="1.5rem" height="1.5rem" />
                <span className="font-medium">{t("Conditions")}</span>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <ToggleSwitchComponent
                disabled={!canEditChecklist}
                checked={allowMultiple}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const next = e.target.checked;
                  setAllowMultiple(next);
                  persist(choices, next);
                }}
                bgColor="default"
                label={t("Allow multiple selection of choices")}
              />
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default ChecklistProperties;
